import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { isCancel } from 'axios'
import { listInvoices } from '@/api/invoices'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { InvoiceStatusBadge } from '@/components/InvoiceStatusBadge'
import { InvoiceReference } from '@/components/invoice/InvoiceReference'
import { formatCurrency, formatDate } from '@/lib/format'
import { formatInvoiceLabel, getOutstandingRefund, summarizeInvoices } from '@/lib/invoice'
import type { Invoice } from '@/api/types'

const LOADING_MESSAGE = 'Laddar fakturor...'
const ERROR_MESSAGE = 'Kunde inte hämta fakturor just nu.'
const EMPTY_MESSAGE = 'Inga fakturor ännu – de skapas automatiskt när leveranser skickas.'
const NO_INVOICES_LABEL = 'Inga fakturor ännu'
const SHOW_ALL_LABEL = 'Visa alla'
const REMAINING_LABEL = 'Kvar'
const REFUND_DUE_LABEL = 'Återbetalning krävs'
const EMPTY_VALUE = '–'

interface CustomerInvoicesCardProps {
  customerId: number
  identityNumber: string | null
}

function formatInvoiceCount(count: number): string {
  const noun = count === 1 ? 'faktura' : 'fakturor'

  return `${count} ${noun}`
}

function formatOptionalDate(value: string | null): string {
  if (value === null) {
    return EMPTY_VALUE
  }

  return formatDate(value)
}

function InvoiceListItem({ invoice }: { invoice: Invoice }): React.ReactElement {
  const remaining = Number(invoice.remaining_sum)
  const hasRemaining = remaining > 0 && invoice.status !== 'voided'
  const refundDue = getOutstandingRefund(invoice)
  const hasRefundDue = refundDue > 0

  return (
    <li className="flex items-center justify-between gap-3 px-3 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Link
            to={`/invoices/${invoice.id}`}
            className="font-medium hover:underline"
          >
            {formatInvoiceLabel(invoice)}
          </Link>
          <InvoiceStatusBadge status={invoice.status} />
        </div>
        <p className="text-sm text-muted-foreground">
          {formatOptionalDate(invoice.invoice_date)} · <InvoiceReference invoice={invoice} />
        </p>
      </div>
      <div className="shrink-0 text-right tabular-nums">
        <p>{formatCurrency(Number(invoice.amount))}</p>
        {hasRemaining && (
          <p className="text-sm text-muted-foreground">
            {REMAINING_LABEL} {formatCurrency(remaining)}
          </p>
        )}
        {hasRefundDue && (
          <p className="text-sm font-medium text-amber-700">
            {REFUND_DUE_LABEL} {formatCurrency(refundDue)}
          </p>
        )}
      </div>
    </li>
  )
}

export function CustomerInvoicesCard({ customerId, identityNumber }: CustomerInvoicesCardProps): React.ReactElement {
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const count = invoices.length
  const hasInvoices = count > 0
  const hasError = errorMessage !== null
  const isEmpty = !isLoading && !hasError && !hasInvoices
  const summary = summarizeInvoices(invoices)
  const hasOutstanding = summary.outstanding > 0
  const hasShowAllLink = hasInvoices && identityNumber !== null
  const showAllHref = `/invoices?search=${encodeURIComponent(identityNumber ?? '')}`
  const countLabel = hasInvoices ? formatInvoiceCount(count) : NO_INVOICES_LABEL
  const hasRefundDue = summary.refundDue > 0
  const outstandingLabel = hasOutstanding ? ` · Utestående ${formatCurrency(summary.outstanding)}` : ''
  const refundLabel = hasRefundDue ? ` · ${REFUND_DUE_LABEL} ${formatCurrency(summary.refundDue)}` : ''
  const description = `${countLabel}${outstandingLabel}${refundLabel}`

  useEffect(() => {
    const controller = new AbortController()

    const loadInvoices = async (): Promise<void> => {
      setIsLoading(true)
      setErrorMessage(null)

      try {
        const data = await listInvoices({ customer_id: customerId }, controller.signal)

        setInvoices(data)
      } catch (error) {
        if (isCancel(error)) return

        setErrorMessage(ERROR_MESSAGE)
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      }
    }

    void loadInvoices()

    return () => controller.abort()
  }, [customerId])

  return (
    <Card className="shadow-none border-border/80">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle>Fakturor</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        {hasShowAllLink && (
          <Button
            asChild
            variant="outline"
            className="shrink-0 rounded-none"
          >
            <Link to={showAllHref}>{SHOW_ALL_LABEL}</Link>
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isLoading && <p className="text-sm text-muted-foreground">{LOADING_MESSAGE}</p>}
        {hasError && <p className="text-sm text-destructive">{errorMessage}</p>}
        {isEmpty && <p className="text-sm text-muted-foreground">{EMPTY_MESSAGE}</p>}
        {hasInvoices && (
          <ul className="max-h-72 divide-y divide-border/60 overflow-y-auto border border-border/70">
            {invoices.map((invoice) => (
              <InvoiceListItem
                key={invoice.id}
                invoice={invoice}
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
